import { set } from "lodash";

import {
  AnyProgressBarWidget,
  AnyProgressBarWidgetStyles,
  DiscountMessageBoxWidget,
  DiscountQuantityWidget,
  DiscountQuantityWidgetStyles,
  DiscountQuantityWidgetVariant,
  DottedJoin,
  WidgetDto,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import { toBuyMoreTemplateReplacements } from "../../features/personalizations/utils";
import { IWidgetsState } from "../types/widgets";

const sliceInitialWidgetsState: IWidgetsState = {
  isSuccess: false,
  widgets: [],
  selectedWidget: undefined,
};

const widgetsSlice = rtk.createSlice({
  name: "widgets",
  initialState: sliceInitialWidgetsState,
  reducers: {
    resetWidgets: (draft) => {
      draft.isSuccess = false;
      draft.widgets = [];
      draft.selectedWidget = undefined;
    },
    setWidgets: (draft, action: PayloadAction<WidgetDto[]>) => {
      draft.isSuccess = true;
      draft.widgets = action.payload ?? [];
      return draft;
    },
    updateWidgetAfterPut: (draft, action: PayloadAction<WidgetDto>) => {
      const index = draft.widgets.findIndex(
        (widget) => widget.id === action.payload.id
      );
      if (index > -1) {
        draft.widgets[index] = action.payload;
      } else {
        draft.widgets.push(action.payload);
      }
      return draft;
    },
    setSelectedWidget: (
      draft,
      action: PayloadAction<
        | WidgetDto
        | AnyProgressBarWidget
        | DiscountQuantityWidget
        | DiscountMessageBoxWidget
        | undefined
      >
    ) => {
      draft.selectedWidget = action.payload;
      return draft;
    },
    setWidgetName: (draft, action: PayloadAction<string>) => {
      if (draft.selectedWidget) {
        draft.selectedWidget.name = action.payload;
      }
      return draft;
    },
    setSitewideWidgetThreshold: (draft, action: PayloadAction<number>) => {
      if (draft.selectedWidget) {
        set(
          draft.selectedWidget,
          `config.desktop.variables.defaultShippingThreshold`,
          action.payload
        );
      }
      return draft;
    },
    setSitewideWidgetEnabled: (draft) => {
      if (draft.selectedWidget) {
        draft.selectedWidget.enabledSitewide =
          !draft.selectedWidget.enabledSitewide;
      }
      return draft;
    },
    setSelectedWidgetVariantSettings: (
      draft,
      action: PayloadAction<{ key: string; configType: "mobile" | "desktop" }>
    ) => {
      if (draft.selectedWidget) {
        set(
          draft.selectedWidget || {},
          `config[${action.payload.configType}].variant`,
          action.payload.key as DiscountQuantityWidgetVariant
        );
      }
      return draft;
    },

    setQuantityWidgetStyleSettings: (
      draft,
      action: PayloadAction<{
        key:
          | DottedJoin<DiscountQuantityWidgetStyles>
          | DottedJoin<AnyProgressBarWidgetStyles>;
        value: string;
      }>
    ) => {
      if (draft.selectedWidget) {
        set(
          draft.selectedWidget || {},
          action.payload.key,
          action.payload.value
        );
      }
      return draft;
    },
    initializeProgressBarVariableSettings: (
      draft,
      action: PayloadAction<{
        configType: "mobile" | "desktop";
      }>
    ) => {
      if (draft.selectedWidget) {
        if (action.payload.configType === "desktop") {
          if (
            !draft.selectedWidget.config.desktop?.variables?.textReplacements
          ) {
            set(
              draft.selectedWidget,
              "config.desktop.variables.textReplacements",
              {}
            );
          }
          if (
            !(draft.selectedWidget as AnyProgressBarWidget).config.desktop
              ?.variables?.textReplacements?.allTiersUnlockedTemplate
          ) {
            set(
              draft.selectedWidget,
              "config.desktop.variables.textReplacements.allTiersUnlockedTemplate",
              "Congratulations, you've unlocked Free Shipping!"
            );
          }
        } else {
          if (
            !draft.selectedWidget.config.mobile?.variables?.textReplacements
          ) {
            set(
              draft.selectedWidget,
              "config.mobile.variables.textReplacements",
              {}
            );
          }
          if (
            !(draft.selectedWidget as AnyProgressBarWidget).config.mobile
              ?.variables?.textReplacements?.allTiersUnlockedTemplate
          ) {
            set(
              draft.selectedWidget,
              "config.mobile.variables.textReplacements.allTiersUnlockedTemplate",
              "Congratulations, you've unlocked Free Shipping!"
            );
          }
        }
      }
      return draft;
    },
    setProgressBarWidgetTextReplacements: (
      draft,
      action: PayloadAction<{
        configType: "mobile" | "desktop";
        value: string;
        templateKey: string;
      }>
    ) => {
      if (draft.selectedWidget) {
        if (
          !(draft.selectedWidget as WidgetDto).config[action.payload.configType]
            .variables.textReplacements
        ) {
          (draft.selectedWidget as WidgetDto).config[
            action.payload.configType
          ].variables.textReplacements = {};
        }
        (draft.selectedWidget as WidgetDto).config[
          action.payload.configType
        ].variables.textReplacements[action.payload.templateKey] =
          toBuyMoreTemplateReplacements(action.payload.value);
      }
      return draft;
    },
  },
  selectors: {
    selectedWidgets: (state) => state.widgets,
    selectedWidget: (state) => state.selectedWidget,
  },
});

export const {
  resetWidgets,
  setSelectedWidget,
  setWidgetName,
  setSitewideWidgetThreshold,
  setSitewideWidgetEnabled,
  setSelectedWidgetVariantSettings,
  setQuantityWidgetStyleSettings,
  initializeProgressBarVariableSettings,
  setProgressBarWidgetTextReplacements,
  setWidgets,
  updateWidgetAfterPut,
} = widgetsSlice.actions;

export const widgetsReducer = widgetsSlice.reducer;
export const { selectedWidgets, selectedWidget } = widgetsSlice.selectors;
